import { useARDashboard,getWidgets as getARWidgets, AR_DASHBOARD } from './useARDashboard';
import { useGLDashboard, getWidgets as getGLWidgets, GL_DASHBOARD } from './useGLDashboard';
import { usePODashboard, getWidgets as getPOWidgets, PO_DASHBOARD } from './usePODashboard';
import { DashboardWidget } from './DashboardWidget';
import {ARFilter} from './filters/ARFilter';
import {POFilter} from './filters/POFilter';
import {GLFilter} from './filters/GLFilter';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { annotateWidgetsWithPermission } from './permissionUtil';
import useUserSettingStore from './UserSettingStore';
import userProfileStore from '../core/UserProfileStore';

const styles = {
  container: {},
  flexWrapper: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '16px',
    alignItems: 'stretch',
  },
  mediumWidget: {
    flex: '1 1 300px',
    display: 'flex',
    flexDirection: 'column' as const,
  },
  largeWidget: {
    flex: '1 1 420px',
    display: 'flex',
    flexDirection: 'column' as const,
  },
  extraLargeWidget: {
    flex: '1 0 766px',
    display: 'flex',
    maxWidth: '766px',
    flexDirection: 'column' as const,
  },
  standardWidget: {
    flex: '1 1 280px',
  },
  cardWidget: {
    flex: '1 1 320px',
    display: 'flex',
    gap: '32px',
    flexDirection: 'column' as const,
    maxWidth: '350px',
  },
  extraLargeAccountBalanceWidget: {
    flex: '1 0 966px',
    display: 'flex',
    maxWidth: '966px',
    flexDirection: 'column' as const,
  },
  emptyState: {
    padding: '24px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    color: '#4b5563',
    fontSize: '14px',
  },
};

export const useDashboardRegistry = () => {

  const { t } = useTranslation();
  const { dashboardAccessSetting } = useUserSettingStore();
  const { permissions } = userProfileStore();
  
  const arPermissionWidgets = useMemo(() => {
    return annotateWidgetsWithPermission(
      getARWidgets(t),
      AR_DASHBOARD.DOMAIN_ID,
      dashboardAccessSetting,
      permissions
    );
  }, [dashboardAccessSetting, permissions, t]);

  const glPermissionWidgets = useMemo(() => {
    return annotateWidgetsWithPermission(
      getGLWidgets(t),
      GL_DASHBOARD.DOMAIN_ID,
      dashboardAccessSetting,
      permissions
    );
  }, [dashboardAccessSetting, permissions, t]);

  const poPermissionWidgets = useMemo(() => {
    return annotateWidgetsWithPermission(
      getPOWidgets(t),
      PO_DASHBOARD.DOMAIN_ID,
      dashboardAccessSetting,
      permissions
    );
  }, [dashboardAccessSetting, permissions, t]);

  // Enable the data fetch only when the user can access at least one widget,
  // so we don't call the API for a dashboard the user has no access to.
  const arDashboard = useARDashboard({
    enabled: Array.isArray(arPermissionWidgets) && arPermissionWidgets.some((w) => w.hasPermission),
    filteredWidgets: arPermissionWidgets,
  });
  const glDashboard = useGLDashboard({
    enabled: Array.isArray(glPermissionWidgets) && glPermissionWidgets.some((w) => w.hasPermission),
    filteredWidgets: glPermissionWidgets,
  });
  const poDashboard = usePODashboard({
    enabled: Array.isArray(poPermissionWidgets) && poPermissionWidgets.some((w) => w.hasPermission),
    filteredWidgets: poPermissionWidgets,
  });

  const dashboards = [
    { id: AR_DASHBOARD.DASHBOARD_ID, ...arDashboard },
    { id: GL_DASHBOARD.DASHBOARD_ID, ...glDashboard },
    { id: PO_DASHBOARD.DASHBOARD_ID, ...poDashboard },
  ] as any;

  return {
    arDashboard,
    glDashboard,
    poDashboard,
    getWidgetState: (dashboardId: string, widgetId: string,) => {
      const dashboard = dashboards.find((d: any) => d.id === dashboardId);
      const widget = dashboard.widgets?.find((w: any) => w.id === widgetId);
      
      return {
        isLoading: dashboard.isLoading,
        error: typeof dashboard.getWidgetError === 'function'
          ? dashboard.getWidgetError(widget)
          : dashboard.error,
        widgetData: widget ? dashboard.getWidgetData(widget) : null,
        widget: widget,
        filter: dashboard.filter,
      };
    },
  };
};
export const AccountingDashboard = () => {
  const { getWidgetState, arDashboard, glDashboard, poDashboard } = useDashboardRegistry();
  const { t } = useTranslation();
  const arWidgets = arDashboard?.widgets || [];
  const poWidgets = poDashboard?.widgets || [];
  const glWidgets = glDashboard?.widgets || [];
  const glUnpostedWidget = glWidgets.find(
    (widget: any) => widget.id === GL_DASHBOARD.WIDGET_UNPOSTED_TRANS_ID
  );
  const glUnpostedErrorWidget = glWidgets.find(
    (widget: any) => widget.id === GL_DASHBOARD.WIDGET_UNPOSTED_TRANS_ERR_ID
  );
  const glAccountBalanceWidget = glWidgets.find(
    (widget: any) => widget.id === GL_DASHBOARD.WIDGET_ACCT_BAL_ID
  );
  const isAnyDashboardLoading = arDashboard?.isLoading || poDashboard?.isLoading || glDashboard?.isLoading;
  const hasVisibleWidget = arWidgets.length > 0 || poWidgets.length > 0 || glWidgets.length > 0;
  const shouldShowEmptyState = !isAnyDashboardLoading && !hasVisibleWidget;

  return (
    <div style={styles.container}>
      {shouldShowEmptyState && (
        <div style={styles.emptyState}>
          {t('dashboard.insufficientPermissions', 'This chart is unavailable as you do not have required permissions.')}
        </div>
      )}
      <div style={styles.flexWrapper}>
        {arWidgets.map((widget: any) => (
          <div key={widget.id} style={{ ...styles.mediumWidget }}>
            <DashboardWidget
              state={getWidgetState(AR_DASHBOARD.DASHBOARD_ID, widget.id)}
              filterComponent={ARFilter}
            />
          </div>
        ))}

        {poWidgets.map((widget: any) => (
          <div key={widget.id} style={{ ...styles.extraLargeWidget }}>
            <DashboardWidget
              state={getWidgetState(PO_DASHBOARD.DASHBOARD_ID, widget.id)}
              filterComponent={POFilter}
            />
          </div>
        ))}

        {(glUnpostedWidget || glUnpostedErrorWidget) && (
          <div style={{ ...styles.cardWidget }}>
            {glUnpostedWidget && (
              <div data-testid={`widget-${GL_DASHBOARD.WIDGET_UNPOSTED_TRANS_ID}`}>
                <DashboardWidget
                  state={getWidgetState(
                    GL_DASHBOARD.DASHBOARD_ID,
                    GL_DASHBOARD.WIDGET_UNPOSTED_TRANS_ID,
                  )}
                  filterComponent={GLFilter}
                  loaderCount={2}
                />
              </div>
            )}
            {glUnpostedErrorWidget && (
              <div data-testid={`widget-${GL_DASHBOARD.WIDGET_UNPOSTED_TRANS_ERR_ID}`}>
                <DashboardWidget
                  state={getWidgetState(
                    GL_DASHBOARD.DASHBOARD_ID,
                    GL_DASHBOARD.WIDGET_UNPOSTED_TRANS_ERR_ID,
                  )}
                  filterComponent={GLFilter}
                  loaderCount={3}
                />
              </div>
            )}
          </div>
        )}

        {glAccountBalanceWidget && (
          <div style={{ ...styles.extraLargeAccountBalanceWidget }}>
            <DashboardWidget
              state={getWidgetState(
                GL_DASHBOARD.DASHBOARD_ID,
                GL_DASHBOARD.WIDGET_ACCT_BAL_ID,
              )}
              filterComponent={GLFilter}
              loaderCount={12}
            />
          </div>
        )}
      </div>
    </div>
  );
};
